/**
 * Qué materiales se ofrecen al registrar una actuación
 * (2026-07-31 equipos · 2026-08-15 toda actuación · 2026-08-31 baja lógica).
 *
 * Dos criterios ortogonales en un solo sitio:
 *
 *     visible ⟺ (activo O ya seleccionado) Y (equipos OK O ya seleccionado)
 *
 * ── Visibilidad por equipo ─────────────────────────────────────────────────
 *
 * Semántica espejo de `tenantIds` en el backend: un material SIN equipos es
 * universal. Se aplica en TODA actuación, básica o normal — desde 2026-08-15,
 * porque las normales las rellena también la gente de tablet y un operario no
 * debe ver material reservado a otro equipo por el hecho de no estar en un
 * recorrido. Antes solo se ejecutaba en la básica.
 *
 * La intersección con `tenantTeamIds` es lo que hace esto seguro entre
 * tenants: los materiales de catálogo antiguos son GLOBALES (`tenantIds: []`),
 * o sea COMPARTIDOS. Si un tenant les asigna uno de SUS equipos, el material
 * debe seguir siendo universal para los demás tenants — que no conocen ese
 * equipo y no deberían perder el material de su listado.
 *
 * Backend hermano: `mergeTeamIdsForTenant` en
 * rois-back/src/modules/action/material/material.service.ts
 * Spec: docs/superpowers/specs/2026-07-31-materiales-por-equipo-design.md
 */

import { getSessionStorageUser } from './sessionStorage';

export type ScopedMaterial = {
  id: string;
  teamIDs?: string[] | null;
  /**
   * Baja lógica del catálogo. 🔴 AUSENTE o `null` = ACTIVO — el backend lo deja
   * nulo a propósito para no necesitar backfill, así que tratar la ausencia
   * como «inactivo» vaciaría el catálogo entero.
   */
  isActive?: boolean | null;
};

/**
 * Ids de los materiales YA seleccionados, sea cual sea la forma del borrador.
 *
 * `materialsTable` es un **mapa por id** (`{ '<materialId>': { id, state } }`)
 * en los dos flujos: lo escribe el paso de materiales y lo consume `saveData`
 * con `Object.values`. El loader de creación, en cambio, lo trataba como array
 * y hacía `.map` sobre él → `TypeError` → el `catch` fail-open devolvía el
 * catálogo SIN FILTRAR. Solo se salvaba la primera visita al paso, cuando el
 * borrador aún no existe y el valor llega `undefined` — que es justo el único
 * caso que cubrían los tests.
 *
 * Punto único a propósito: los dos loaders normalizaban esto por su cuenta y
 * con criterios distintos (`Object.keys` en validación, `.map` en creación).
 */
export const collectSelectedMaterialIds = (
  source: unknown
): string[] => {
  if (!source) return [];

  // Forma canónica: mapa por id. Las claves ya SON los ids de material.
  if (!Array.isArray(source) && typeof source === 'object') {
    return Object.entries(source as Record<string, unknown>).flatMap(
      ([key, row]) => {
        const fromRow =
          row && typeof row === 'object'
            ? ((row as { id?: string; materialId?: string }).id ??
              (row as { materialId?: string }).materialId)
            : undefined;
        // La clave manda (es el id del catálogo); la fila es el respaldo.
        return [key, fromRow].filter((id): id is string => Boolean(id));
      }
    );
  }

  // Tolerancia: si algún camino lo pasa como lista de filas.
  if (Array.isArray(source)) {
    return source
      .map((row) =>
        row && typeof row === 'object'
          ? ((row as { id?: string; materialId?: string }).id ??
            (row as { materialId?: string }).materialId)
          : typeof row === 'string'
            ? row
            : undefined
      )
      .filter((id): id is string => Boolean(id));
  }

  return [];
};

export const scopeMaterialsForWizard = <T extends ScopedMaterial>({
  materials,
  tenantTeamIds,
  orderTeamIds,
  userTeamIds,
  keepIds,
}: {
  materials: T[];
  tenantTeamIds: string[];
  /** Equipos de la OT padre. Vacío = ese criterio no se aplica. */
  orderTeamIds?: string[];
  /** Equipos del usuario logueado. Vacío = ese criterio no se aplica. */
  userTeamIds?: string[];
  keepIds?: Iterable<string>;
}): T[] => {
  const tenantTeams = new Set(tenantTeamIds ?? []);
  const orderTeams = new Set(orderTeamIds ?? []);
  const userTeams = new Set(userTeamIds ?? []);
  const keep = new Set(keepIds ?? []);

  return (materials ?? []).filter((material) => {
    // Regla dura: lo ya seleccionado NUNCA se oculta. Al validar o reabrir una
    // actuación antigua sus filas guardadas siguen ahí; ocultar el material
    // del catálogo haría desaparecer datos reales de la pantalla.
    if (keep.has(material.id)) return true;

    // Baja lógica del catálogo. Va DESPUÉS del `keep` a propósito: una
    // actuación que ya usa un material desactivado lo sigue mostrando, y al
    // validarla se guarda igual.
    //
    // 🔴 Sin esa precedencia, desactivar un material lo BORRARÍA de las
    // actuaciones abiertas la próxima vez que alguien las guardara: el guardado
    // compara el servidor contra lo que la pantalla tiene, y lo que no está en
    // la pantalla se interpreta como quitado. Es el modo de fallo de «la básica
    // sin fauna».
    //
    // Se compara contra `false` y NO por veracidad: `null`/ausente es ACTIVO.
    if (material.isActive === false) return false;

    const restrictedToTenantTeams = (material.teamIDs ?? []).filter((id) =>
      tenantTeams.has(id)
    );

    // Sin restricciones DE ESTE TENANT → universal.
    if (restrictedToTenantTeams.length === 0) return true;

    // Los dos criterios se aplican solo si HAY con qué aplicarlos, y cuando
    // ambos aplican tienen que cumplirse LOS DOS:
    //
    //  - equipos del usuario: un operario de ÈXSER no ve material reservado a
    //    TRAM 1, esté en la actuación que esté. Los supervisores (coex,
    //    capataz, room-team, super-admin) no tienen equipo, así que para ellos
    //    este criterio no aplica y siguen viendo el catálogo entero.
    //  - equipos de la OT padre: es lo que ya filtraba la básica del RV, y se
    //    mantiene para que un supervisor que valida una básica siga viendo solo
    //    lo del equipo de ese recorrido.
    //
    // Si no hay ninguno de los dos (usuario sin equipo y OT sin equipo) no hay
    // criterio que aplicar y el material se muestra: antes de más que dejar al
    // operario sin catálogo.
    const passesUserTeams =
      userTeams.size === 0 ||
      restrictedToTenantTeams.some((id) => userTeams.has(id));

    const passesOrderTeams =
      orderTeams.size === 0 ||
      restrictedToTenantTeams.some((id) => orderTeams.has(id));

    return passesUserTeams && passesOrderTeams;
  });
};

/**
 * Equipos del usuario logueado, leídos de la sesión.
 *
 * Los loaders corren fuera de React, así que no hay hooks: se lee del mismo
 * `sessionStorage` que el resto de la app (mismo patrón que `getCurrentRole`
 * en `gql/selectors/actions.ts`). Si la sesión no se puede leer se devuelve
 * lista vacía, que significa «este criterio no aplica» — fail-open.
 */
export const getCurrentUserTeamIds = (): string[] => {
  try {
    const session = getSessionStorageUser();
    const teamIds = session?.user?.teamIDs;
    return Array.isArray(teamIds) ? teamIds.filter(Boolean) : [];
  } catch {
    return [];
  }
};
